import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const maxHeight = 300;

export default function useExpandableResult(textAreaRef, contentRef, children, value) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeout = useRef<ReturnType<typeof window.setTimeout>>(null);

  const shouldUseTextArea = value !== undefined && !children;

  const fullHeight = useMemo(() => {
    if (shouldUseTextArea && textAreaRef.current) {
      return textAreaRef.current.scrollHeight; // Here we don't need the 20+ pixels
    } else if (contentRef.current) {
      return Math.max(contentRef.current.scrollHeight, contentRef.current.offsetHeight); // Adding 20 here to give space to Collapse button;
    }
  }, [shouldUseTextArea, textAreaRef.current, contentRef.current, children, value]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const onScroll = useCallback(() => {
    if (isExpanded) return;
    setIsScrolling(true);

    clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      setIsScrolling(false);
    }, 800);
  }, [isExpanded]);

  useEffect(() => {
    const el = textAreaRef.current || contentRef.current;
    el.addEventListener('scroll', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  const displayHeight = (() => {
    if (isExpanded || !fullHeight || fullHeight < maxHeight) return fullHeight;
    return maxHeight;
  })();

  const needsExpansion = fullHeight > maxHeight;

  return {
    needsExpansion,
    isExpanded,
    isScrolling,
    displayHeight,
    toggleExpand,
    maxHeight,
  };
}
